import { z } from 'zod';

// Schema de validação para criação/edição de usuários
export const profileSchema = z.object({
  email: z.string()
    .email('Email inválido')
    .max(255, 'Email muito longo')
    .toLowerCase()
    .trim()
    .optional()
    .or(z.literal('')),
  full_name: z.string()
    .min(1, 'Nome completo é obrigatório')
    .max(200, 'Nome muito longo')
    .trim(),
  role: z.enum(['ADMIN', 'SUPERVISOR', 'PADRAO', 'VISUALIZACAO'], {
    errorMap: () => ({ message: 'Papel inválido' })
  }),
  matricula: z.string()
    .min(1, 'Matrícula é obrigatória')
    .max(50, 'Matrícula muito longa'),
  telefone: z.string()
    .max(20, 'Telefone muito longo')
    .nullable()
    .optional(),
  nome_guerra: z.string()
    .max(100, 'Nome de guerra muito longo')
    .nullable()
    .optional(),
  unidade_lotacao: z.string()
    .max(200, 'Unidade de lotação muito longa')
    .nullable()
    .optional(),
  habilidades_competencias: z.string()
    .max(1000, 'Habilidades muito longas')
    .nullable()
    .optional(),
  observacoes: z.string()
    .max(1000, 'Observações muito longas')
    .nullable()
    .optional(),
  avatar_url: z.string()
    .url('URL inválida')
    .nullable()
    .optional()
    .or(z.literal('')),
  usuario_sistema: z.boolean().optional(),
  pode_ser_membro_equipe: z.boolean().optional(),
  can_create_projects: z.boolean().optional(),
  can_create_contracts: z.boolean().optional(),
  can_create_action_plans: z.boolean().optional(),
  can_create_strategic_items: z.boolean().optional(),
  // Flags de visibilidade granular por módulo (default true no banco)
  can_view_dashboard: z.boolean().optional(),
  can_view_projects: z.boolean().optional(),
  can_view_contracts: z.boolean().optional(),
  can_view_portfolios: z.boolean().optional(),
  can_view_strategic: z.boolean().optional(),
  can_view_calendar: z.boolean().optional(),
  can_view_tasks: z.boolean().optional(),
  can_view_reports_overview_projects: z.boolean().optional(),
  can_view_reports_overview_contracts: z.boolean().optional(),
  can_view_reports_financial: z.boolean().optional(),
  can_view_reports_users: z.boolean().optional(),
  can_view_reports_areas: z.boolean().optional(),
});

// Schema de validação para contratos (base sem refine para poder usar .partial())
const contractBaseSchema = z.object({
  contract_number: z.string()
    .min(1, 'Número do contrato é obrigatório')
    .max(50, 'Número muito longo')
    .regex(/^[A-Z0-9\/\-]+$/i, 'Formato inválido (use apenas letras, números, / e -)'),
  main_process_id: z.string()
    .max(50, 'Número do processo muito longo')
    .nullable()
    .optional(),
  total_value: z.number()
    .positive('Valor deve ser positivo')
    .max(1000000000, 'Valor excede limite de R$ 1 bilhão')
    .min(0.01, 'Valor mínimo é R$ 0,01'),
  start_date: z.string()
    .datetime('Data inicial inválida'),
  end_date: z.string()
    .datetime('Data final inválida'),
  independent_contract_name: z.string()
    .max(200, 'Nome muito longo')
    .nullable()
    .optional(),
  independent_contract_object: z.string()
    .max(2000, 'Objeto muito longo')
    .nullable()
    .optional(),
  responsible_unit_name: z.string()
    .max(200, 'Nome da unidade muito longo')
    .nullable()
    .optional(),
  demanding_unit_name: z.string()
    .max(200, 'Nome da unidade muito longo')
    .nullable()
    .optional(),
  procurement_regime: z.enum(['8666', '14133'])
    .nullable()
    .optional(),
  scheduled_renewal_months: z.number()
    .int('Deve ser um número inteiro')
    .positive('Deve ser positivo')
    .max(120, 'Máximo de 120 meses (10 anos)')
    .nullable()
    .optional(),
  has_price_registry: z.boolean().optional(),
});

// Schema completo com validação de datas
export const contractSchema = contractBaseSchema.refine(
  (data) => new Date(data.end_date) > new Date(data.start_date),
  {
    message: 'Data final deve ser posterior à data inicial',
    path: ['end_date']
  }
);

// Schema de validação para pagamentos de contrato
export const contractPaymentSchema = z.object({
  payment_date: z.string()
    .datetime('Data de pagamento inválida'),
  amount: z.number()
    .positive('Valor deve ser positivo')
    .max(1000000000, 'Valor excede limite de R$ 1 bilhão')
    .min(0.01, 'Valor mínimo é R$ 0,01'),
  nf_number: z.string()
    .max(50, 'Número da nota fiscal muito longo')
    .nullable()
    .optional(),
  empenho_number: z.string()
    .max(50, 'Número do empenho muito longo')
    .nullable()
    .optional(),
  notes: z.string()
    .max(1000, 'Observações muito longas')
    .nullable()
    .optional(),
});

// Schema de validação para projetos
export const projectSchema = z.object({
  // Identificação básica
  name: z.string()
    .min(1, 'Nome do projeto é obrigatório')
    .max(300, 'Nome muito longo')
    .trim(),
  nickname: z.string()
    .max(100, 'Apelido muito longo')
    .nullable()
    .optional(),
  object: z.string()
    .max(2000, 'Objeto muito longo')
    .nullable()
    .optional(),
  sei_process_id: z.string()
    .max(50, 'Número do processo SEI muito longo')
    .nullable()
    .optional(),
  
  // Valores financeiros
  value: z.number()
    .positive('Valor deve ser positivo')
    .max(1000000000, 'Valor excede limite de R$ 1 bilhão')
    .nullable()
    .optional(),
  valor_investimento: z.number()
    .nonnegative('Valor de investimento não pode ser negativo')
    .max(1000000000, 'Valor excede limite')
    .nullable()
    .optional(),
  valor_custeio: z.number()
    .nonnegative('Valor de custeio não pode ser negativo')
    .max(1000000000, 'Valor excede limite')
    .nullable()
    .optional(),
  valor_previsto_pios: z.number()
    .nonnegative('Valor previsto não pode ser negativo')
    .max(1000000000, 'Valor excede limite')
    .nullable()
    .optional(),
  tipo_orcamento: z.enum(['INVESTIMENTO', 'CUSTEIO', 'MISTO']).nullable().optional(),
  
  // Configurações e flags
  priority: z.enum(['BAIXA', 'NORMAL', 'ALTA', 'URGENTE'], {
    errorMap: () => ({ message: 'Prioridade inválida' })
  }).optional(),
  responsible_unit: z.string()
    .max(200, 'Unidade responsável muito longa')
    .nullable()
    .optional(),
  is_private: z.boolean().optional(),
  is_in_pca: z.boolean().optional(),
  is_favorite: z.boolean().optional(),
  is_archived: z.boolean().optional(),
  observations: z.string()
    .max(5000, 'Observações muito longas')
    .nullable()
    .optional(),
  
  // Relacionamentos e IDs
  demanding_unit_id: z.string().uuid().nullable().optional(),
  sgti_coordinator_id: z.string().uuid().nullable().optional(),
  requisitante_id: z.string().uuid().nullable().optional(),
  tecnico_id: z.string().uuid().nullable().optional(),
  administrativo_id: z.string().uuid().nullable().optional(),
  parecerista_id: z.string().uuid().nullable().optional(),
  status: z.string().max(100).optional(),
  linked_projects: z.array(z.string().uuid()).nullable().optional(),
  editor_ids: z.array(z.string().uuid()).nullable().optional(),
  
  // Prazos e datas (strings ISO)
  etp_deadline: z.string().nullable().optional(),
  tr_deadline: z.string().nullable().optional(),
  mgr_deadline: z.string().nullable().optional(),
  team_nomination_date: z.string().nullable().optional(),
  estimated_bidding_date: z.string().nullable().optional(),
  
  // Configuração de cálculo automático
  auto_calculate_deadlines: z.boolean().optional(),
  deadline_overrides: z.record(z.any()).nullable().optional(),
  
  // Tags customizadas
  custom_tags: z.array(z.object({
    name: z.string(),
    color: z.string(),
    backgroundColor: z.string().optional(),
    borderColor: z.string().optional(),
  })).nullable().optional(),
  
  // Dados estruturados (JSONs)
  etp_info: z.record(z.any()).nullable().optional(),
  mgr_info: z.record(z.any()).nullable().optional(),
  tr_info: z.record(z.any()).nullable().optional(),
  external_phase_info: z.record(z.any()).nullable().optional(),
  tasks: z.array(z.any()).nullable().optional(),
  notes: z.array(z.any()).nullable().optional(),
  documents: z.array(z.any()).nullable().optional(),
  additional_members: z.array(z.any()).nullable().optional(),
  
  // Outras configurações
  coordinator_can_manage_notes: z.boolean().nullable().optional(),
  phase_task_mode: z.record(z.any()).nullable().optional(),


  // Conclusão de artefatos (ETP/MGR/TR)
  etp_completed_at: z.string().nullable().optional(),
  etp_completed_by: z.string().uuid().nullable().optional(),
  mgr_completed_at: z.string().nullable().optional(),
  mgr_completed_by: z.string().uuid().nullable().optional(),
  tr_completed_at: z.string().nullable().optional(),
  tr_completed_by: z.string().uuid().nullable().optional(),
});

// Função auxiliar para validar dados antes de operações de banco
export function validateData<T>(schema: z.ZodSchema<T>, data: unknown): T {
  return schema.parse(data);
}

// Função auxiliar que retorna erros de validação de forma amigável
export function getValidationErrors(error: z.ZodError): string {
  return error.errors.map(err => `${err.path.join('.')}: ${err.message}`).join('; ');
}
